import { Button, PetitionCard, Text } from '@change/ds-core-ui';

import { Translate, TranslatePlural, useI18n } from '@change-corgi/core/react/i18n';
import { VisuallyHidden } from '@change-corgi/design-system-legacy/a11y';
import { Loader } from '@change-corgi/design-system-legacy/components/progressiveDisclosure';

import { useLazyLoadedListFocus } from 'src/app/shared/hooks/focus';

import { useVictoriousPetitionsSection } from './hooks/useVictoriousPetitionsSection';

export const VictoriousPetitionsSection: React.FC = () => {
	const { translate, translatePlural } = useI18n();
	const {
		data: { displayed, displayName, petitions, hasNextPage, nextPageLoading },
		actions: { loadMore },
	} = useVictoriousPetitionsSection();

	const { containerRef, loadingRef, recordListEvent, lastEvent, getListItemRef, loadMoreRef } =
		useLazyLoadedListFocus();

	if (!displayed) {
		return null;
	}

	return (
		<div className="flex flex-col gap-6" data-qa="victories-container">
			<Text as="h2" variant="heading" size="small" className="text-typography-strong mb-6" data-qa="victories-heading">
				<Translate value="corgi.local.victorious_petitions.heading" replacements={{ displayName }} />
			</Text>

			<div ref={containerRef} className="flex flex-col items-center gap-6">
				<div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
					{petitions.map((petition, index) => (
						<div className="flex" key={petition.id}>
							{lastEvent.type === 'loadMore' && index === lastEvent.firstNewItemIndex && (
								<VisuallyHidden role="alert">
									<TranslatePlural
										value="corgi.tag.petitions.fetched_more"
										count={petitions.length - lastEvent.previousItemsCount}
										replacements={{ count: petitions.length - lastEvent.previousItemsCount }}
									/>
								</VisuallyHidden>
							)}

							<PetitionCard
								ref={getListItemRef(index)}
								data-qa="victories-petition-card"
								imageUrl={petition.photo?.petitionMedium.url}
								isVictory
								victoryLabel={translate('corgi.components.petition_card.victory')}
								victoryLabelClassName="capitalize bg-fill-accent-primary-strong"
								signaturesLabel={translatePlural(
									'corgi.components.petition_card.signatures',
									petition.signatureState.signatureCount.displayed,
									{
										countLocalized: petition.signatureState.signatureCount.displayedLocalized,
									},
								)}
								className="w-full"
								location={`${displayName}, US`}
								petitionId={petition.id}
								petitionLinkUrl={`/p/${petition.slug}?source_location=location_page`}
								title={petition.ask}
							/>
						</div>
					))}
				</div>

				{nextPageLoading && (
					<>
						<VisuallyHidden role="alert">
							<Translate value="corgi.tag.petitions.fetching_more" />
						</VisuallyHidden>
						{/* TODO: DS loading state */}
						<Loader size={44} mx="auto" ref={loadingRef} mt={24} />
					</>
				)}

				{!nextPageLoading && hasNextPage && (
					<Button
						size="small"
						ref={loadMoreRef}
						variant="secondary"
						data-qa="victories-load-more-button"
						data-testid="victories-load-more-button"
						onClick={() => {
							recordListEvent({ type: 'loadMore', itemsCount: petitions.length });
							loadMore();
						}}
					>
						<Translate value="corgi.local.victorious_petitions.show_more" />
					</Button>
				)}
			</div>
		</div>
	);
};
